import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchItem } from '../queries';

interface Option {
  label: string;
  value: string;
}

interface OptionsState {
  listId: string;
  options: Option[];
  status: string;
  error: any | null;
}

export interface SfOptionsState {
  [key: string]: OptionsState;
}

// add more configs here for additional option lists from sf
const DESCRIBE_CONFIG = {
  SEServiceType: {
    endpoint: 'Task',
    fieldId: 'SE_Service_Type__c',
    validFor: ['YAAA', 'QAAA'],
  },
  Technology: {
    endpoint: 'Task',
    fieldId: 'Technology__c',
    validFor: ['B//8', 'AAf8'], // can these filter values be pulled from SF instead of hard-coded?
  }
}

const initialState: SfOptionsState = {};

const fetchDescription = async (object: string): Promise<Option[]> => {
  const config = DESCRIBE_CONFIG[object];
  const endpointDescription = await fetchItem(`sf/sobject/${config.endpoint}/describe/`);
  return endpointDescription.fields
  .find(field => field.name === config.fieldId).picklistValues
  .filter(listItem => config.validFor === null || config.validFor.includes(listItem.validFor))
  .map(listItem => ({
    label: listItem.label,
    value: listItem.value,
  }));
}

const getSfOptions = createAsyncThunk(
  `salesforceOptions/options`,
  async (object: string, { getState }) => {
    const state = getState();
    const optionsData = state[object];
    if (optionsData?.status === 'loading' || optionsData?.status === 'success') {
      return optionsData.options;
    }
    const fetchedOptions = await fetchDescription(object);
    return fetchedOptions;
  }
);

const salesforceOptions = createSlice({
  name: 'salesForceOptions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSfOptions.pending, (state, action) => {
      const object = action.meta.arg;
      state[object] = {
        listId: object,
        options: [],
        status: 'loading',
        error: null,
      }
    });
    builder.addCase(getSfOptions.fulfilled, (state, action) => {
      const object = action.meta.arg;
      const data = action.payload;
      state[object] = {
        listId: object,
        options: data,
        status: 'success',
        error: null,
      }
    });
    builder.addCase(getSfOptions.rejected, (state, action) => {
      const object = action.meta.arg;
      state[object] = {
        listId: object,
        options: [],
        status: 'failed',
        error: action.error.message ?? `Failed to fetch ${object}`,
      }
    });
  }
});

export default salesforceOptions.reducer;
export { fetchDescription, getSfOptions };